// deriveBranchEditContext — produces the per-branch child VqeEditContext for
// VQE set-op branch editing (VQE-3 Slice 3).
//
// Each branch of a UNION/INTERSECT/EXCEPT chain needs its own VqeEditContext so
// the nested <SelectRenderer> (Slice 4) can route edits to the correct byte
// range. This helper builds that child context:
//   - synthesises a per-branch StatementInfo (via makeNestedSubqueryStatement)
//     so resolveEdit routes through applyStructuralEditForStatement, not the
//     id-based applyStructuralEdit path
//   - re-resolves the Resolver scope at the branch start offset
//   - spreads document-scoped fields from the parent (service, documentId,
//     model, editor, getLiveSource) — no new service or model
//
// NOTE: for flat unparenthesized set-op chains, resolveScope at the branch
// start returns the COMPOUND-statement scope (all branches' aliases visible),
// not a branch-isolated scope. True per-branch scope isolation is the deferred
// VQE-3 per-branch-scope follow-up (also flagged in resolverService.ts):
// suggestions may over-offer sibling-branch columns, but EDIT CORRECTNESS is
// unaffected — edits are range-driven via the synthetic `statementInfo`.
//
// Pure, no side effects, no class or enum.

import type { SetOperatorBranchSpan } from '../render/select/extractSetOperatorBranches';
import { makeNestedSubqueryStatement } from '../render/select/nestedStatement';
import type { VqeEditContext } from './editContext';

/**
 * Derives the per-branch child `VqeEditContext` for a set-op branch.
 *
 * @param parent           The tab-level (or outer) VqeEditContext.
 * @param parentStatementId The REAL (non-synthetic) statement id of the
 *                          compound statement — from `documentStructure.statements`.
 *                          Never pass `parent.statementId` directly: `parent`
 *                          may already be a branch child (synthetic id), which
 *                          would produce a doubly-nested, non-resolvable id.
 * @param branch           The byte span of the branch (from
 *                          `extractSetOperatorBranches`).
 */
export function deriveBranchEditContext(
  parent: VqeEditContext,
  parentStatementId: string,
  branch: SetOperatorBranchSpan,
): VqeEditContext {
  // Synthetic StatementInfo covering just this branch's byte range. The id
  // uses the `parentStatementId.sub.START-END` form so it is unique per branch
  // and per compound statement, matching the pattern used throughout the codebase
  // (e.g. SubquerySection, and the Slice-2 resolveEdit tests).
  const statementInfo = makeNestedSubqueryStatement(
    parentStatementId,
    branch.start,
    branch.end,
  );

  // Re-resolve the scope at the branch start offset (not the parent's scope).
  // For parenthesized branches this is the branch's own scope; for flat
  // unparenthesized chains it is the compound-statement scope (see file NOTE).
  const scope = parent.service.resolveScope(parent.documentId, {
    offset: branch.start,
    line: 0,
    column: 0,
  });

  // Spread parent to inherit all document-scoped fields unchanged, then
  // override only the branch-specific identity fields.
  return {
    ...parent,
    statementId: statementInfo.id,
    statementInfo,
    scope,
  };
}
